// Rotating, cropping and resizing, as arithmetic before it is anything else.
//
// The order the three happen in is a decision, not an implementation detail.
// Somebody rotates the picture, looks at what is now in front of them, and then
// drags a box around part of it. So rotation comes first and the crop is stored
// in the coordinates of the rotated picture, which is the only way a crop can
// still mean what it looked like it meant. Resizing is last, because it changes
// how many pixels the result has and nothing about what is in it.
//
// The crop is kept as fractions of the rotated frame rather than as pixels, so
// it survives a preview at 1200 across and an export at 6000 without drifting,
// and so a rotation can carry it along instead of throwing it away.

import { sizeOf } from './images.js';

/** Turns the crop can make, in degrees clockwise. */
export const ROTATIONS = [0, 90, 180, 270];

/** Crop shapes offered, `null` meaning whatever the pointer draws. */
export const ASPECTS = [
    { id: 'free',  label: 'Free',     ratio: null },
    { id: '1:1',   label: 'Square',   ratio: 1 },
    { id: '4:3',   label: '4:3',      ratio: 4 / 3 },
    { id: '3:2',   label: '3:2',      ratio: 3 / 2 },
    { id: '16:9',  label: '16:9',     ratio: 16 / 9 },
    { id: '9:16',  label: '9:16',     ratio: 9 / 16 },
    { id: 'og',    label: 'OG image', ratio: 1200 / 630 },
];

/** A crop covering everything. */
export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };

/** The size of the picture once it has been turned. */
export function orientedSize(source, rotation) {
    const quarter = ((rotation % 360) + 360) % 360;
    return quarter === 90 || quarter === 270
        ? { width: source.height, height: source.width }
        : { width: source.width, height: source.height };
}

/** Hold a rectangle inside 0..1, keeping its size where it can. */
export function clampCrop(crop) {
    const w = Math.min(1, Math.max(0.02, crop.w));
    const h = Math.min(1, Math.max(0.02, crop.h));
    return {
        x: Math.min(1 - w, Math.max(0, crop.x)),
        y: Math.min(1 - h, Math.max(0, crop.y)),
        w,
        h,
    };
}

/**
 * The crop, expressed in the frame it will occupy after a quarter turn.
 *
 * Carried along rather than reset, because losing a carefully placed box to a
 * button that was meant to straighten the picture is a small betrayal. A turn
 * clockwise sends the top left corner to the top right, which is where the
 * swap of the two axes and the subtraction below come from.
 */
export function rotateCrop(crop, quarters) {
    let out = { ...crop };
    const turns = ((quarters % 4) + 4) % 4;
    for (let i = 0; i < turns; i += 1) {
        out = { x: 1 - (out.y + out.h), y: out.x, w: out.h, h: out.w };
    }
    return clampCrop(out);
}

/** The crop, mirrored the same way the picture is about to be. */
export function flipCrop(crop, horizontal) {
    return clampCrop(horizontal
        ? { ...crop, x: 1 - (crop.x + crop.w) }
        : { ...crop, y: 1 - (crop.y + crop.h) });
}

/**
 * Fit a crop to a shape without moving its centre.
 *
 * Only ever shrinks one side. Growing the other would push the box past an edge
 * on a picture where it is already against one, and a crop that jumps when a
 * ratio is chosen looks like a bug even when the arithmetic is right.
 */
export function applyAspect(crop, ratio, frame) {
    if (!ratio) return clampCrop(crop);

    // The ratio is a shape on screen, so it has to be measured in pixels and
    // then turned back into fractions, or a portrait picture would get a square
    // crop that is not square.
    const pixelW = crop.w * frame.width;
    const pixelH = crop.h * frame.height;
    const current = pixelW / pixelH;

    let w = crop.w;
    let h = crop.h;
    if (current > ratio) {
        w = (pixelH * ratio) / frame.width;
    } else {
        h = (pixelW / ratio) / frame.height;
    }

    return clampCrop({
        x: crop.x + (crop.w - w) / 2,
        y: crop.y + (crop.h - h) / 2,
        w,
        h,
    });
}

/**
 * The crop after dragging `handle` to `point`.
 *
 * The corner or edge opposite the one being dragged stays put, which is what
 * makes a crop feel like a physical thing. With a shape locked, the dimension
 * the pointer is not driving follows, and both are then held inside the picture
 * by the room actually left on the anchor's side rather than by clamping
 * afterwards, which would quietly break the shape.
 */
export function resizeCrop(handle, point, start, ratio, size) {
    const west = handle.includes('w');
    const east = handle.includes('e');
    const north = handle.includes('n');
    const south = handle.includes('s');

    // Edge handles keep the other axis exactly as it was, unless a shape is
    // locked, in which case it grows and shrinks around the same centre.
    if (!west && !east) {
        const anchorY = north ? start.y + start.h : start.y;
        let h = Math.abs(point.y - anchorY);
        h = Math.min(h, north ? anchorY : 1 - anchorY);
        let w = start.w;
        if (ratio) {
            w = ((h * size.height) * ratio) / size.width;
            const centre = start.x + start.w / 2;
            w = Math.min(w, 2 * Math.min(centre, 1 - centre));
            h = ((w * size.width) / ratio) / size.height;
        }
        const y = point.y < anchorY ? anchorY - h : anchorY;
        const x = ratio ? start.x + start.w / 2 - w / 2 : start.x;
        return clampCrop({ x, y, w, h });
    }

    if (!north && !south) {
        const anchorX = west ? start.x + start.w : start.x;
        let w = Math.abs(point.x - anchorX);
        w = Math.min(w, west ? anchorX : 1 - anchorX);
        let h = start.h;
        if (ratio) {
            h = ((w * size.width) / ratio) / size.height;
            const centre = start.y + start.h / 2;
            h = Math.min(h, 2 * Math.min(centre, 1 - centre));
            w = ((h * size.height) * ratio) / size.width;
        }
        const x = point.x < anchorX ? anchorX - w : anchorX;
        const y = ratio ? start.y + start.h / 2 - h / 2 : start.y;
        return clampCrop({ x, y, w, h });
    }

    // A corner: the opposite corner is the anchor.
    const anchorX = west ? start.x + start.w : start.x;
    const anchorY = north ? start.y + start.h : start.y;

    let w = Math.abs(point.x - anchorX);
    let h = Math.abs(point.y - anchorY);

    if (ratio) {
        // Follow whichever axis the pointer moved further along, so the box
        // tracks the hand rather than fighting it.
        const byWidth = (w * size.width) / ratio / size.height;
        if (byWidth >= h) h = byWidth;
        else w = ((h * size.height) * ratio) / size.width;
    }

    const roomX = point.x < anchorX ? anchorX : 1 - anchorX;
    const roomY = point.y < anchorY ? anchorY : 1 - anchorY;
    if (w > roomX) {
        w = roomX;
        if (ratio) h = ((w * size.width) / ratio) / size.height;
    }
    if (h > roomY) {
        h = roomY;
        if (ratio) w = ((h * size.height) * ratio) / size.width;
    }

    return clampCrop({
        x: point.x < anchorX ? anchorX - w : anchorX,
        y: point.y < anchorY ? anchorY - h : anchorY,
        w,
        h,
    });
}

/** The largest crop of a given shape that fits the whole frame. */
export function largestCrop(ratio, frame) {
    return applyAspect(FULL_CROP, ratio, frame);
}

/** The crop in pixels of the rotated picture. */
export function cropPixels(source, state) {
    const frame = orientedSize(source, state.rotation);
    return {
        x: Math.round(state.crop.x * frame.width),
        y: Math.round(state.crop.y * frame.height),
        width: Math.max(1, Math.round(state.crop.w * frame.width)),
        height: Math.max(1, Math.round(state.crop.h * frame.height)),
    };
}

/**
 * How big the saved file will be.
 *
 * Kept apart from the drawing so the interface can put the number under the
 * controls without rendering anything, the same split compose.js needed.
 */
export function outputSize(source, state) {
    const crop = cropPixels(source, state);
    if (!state.resize.enabled) return { width: crop.width, height: crop.height };

    const width = Math.max(1, Math.round(state.resize.width || crop.width));
    const height = Math.max(1, Math.round(state.resize.height || crop.height));
    return { width, height };
}

/** The height that keeps the crop's shape at a given width, and the reverse. */
export function matchingHeight(source, state, width) {
    const crop = cropPixels(source, state);
    return Math.max(1, Math.round((width * crop.height) / crop.width));
}

export function matchingWidth(source, state, height) {
    const crop = cropPixels(source, state);
    return Math.max(1, Math.round((height * crop.width) / crop.height));
}

/**
 * Draw the picture turned, mirrored and cropped, at `width` x `height`.
 *
 * The whole rotated picture is drawn onto its own canvas first and the crop is
 * taken out of that. Going straight from the source with a transform and a
 * source rectangle in one step is possible and about as fast, but the two
 * coordinate systems have to be reasoned about at once, and every version of
 * this that has been written that way had the flips wrong in one quadrant.
 */
export function renderTransform(image, state, width, height) {
    const source = sizeOf(image);
    const frame = orientedSize(source, state.rotation);

    const oriented = document.createElement('canvas');
    oriented.width = frame.width;
    oriented.height = frame.height;

    const ctx = oriented.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.translate(frame.width / 2, frame.height / 2);
    ctx.rotate((state.rotation * Math.PI) / 180);
    // Applied after the rotation, so "mirror" means what the person sees now
    // rather than what the file happened to hold before they turned it.
    ctx.scale(state.flipX ? -1 : 1, state.flipY ? -1 : 1);
    ctx.drawImage(image, -source.width / 2, -source.height / 2, source.width, source.height);

    const crop = cropPixels(source, state);

    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(width));
    out.height = Math.max(1, Math.round(height));

    const outCtx = out.getContext('2d');
    outCtx.imageSmoothingEnabled = true;
    outCtx.imageSmoothingQuality = 'high';
    outCtx.drawImage(
        oriented,
        crop.x, crop.y, crop.width, crop.height,
        0, 0, out.width, out.height
    );

    return out;
}

/** The rotated picture at preview size, with no crop taken out of it. */
export function renderPreviewFrame(image, state, width) {
    const source = sizeOf(image);
    const frame = orientedSize(source, state.rotation);
    const w = Math.max(1, Math.round(Math.min(width, frame.width)));
    const h = Math.max(1, Math.round((frame.height / frame.width) * w));

    return renderTransform(image, { ...state, crop: FULL_CROP }, w, h);
}
